<template>
  <div class="school-data">
    <div class="school-data__header">
      <div class="school-data__header__left">
        <h1>Vikarkontinuitet</h1>
        <changeSchoolYear :white-theme="true" class="marg-1-right"></changeSchoolYear>
      </div>
      <div class="school-data__header__right">
        <p class="d-link" @click="resetDates" v-if="!isDatesPristine">{{ $t('vue2.task_overview.reset') }}</p>
        <Datepickerrange
          :startdate="startDate"
          :enddate="endDate"
          @startchanged="setStartDate"
          @endchanged="setEndDate">
        </Datepickerrange>
      </div>
    </div>
    <div class="loader-container" v-if="isLoading">
      <Loader></Loader>
      <h4>Snup en kop kaffe, det kan tage et par minutter</h4>
    </div>
    <template v-else>
      <p class="text-explainer">
        Viser for hver klasse og hvert fag, hvor mange forskellige vikarer der har dækket, og hvor stor en andel af vikartimerne den mest brugte vikar har haft.
        En høj andel betyder, at det i høj grad er den samme vikar, der underviser.
      </p>
      <h2 class="text-explainer" v-if="klassRows.length < 1">Ingen vikartimer i perioden</h2>
      <table class="d-table" v-for="klass in klassRows" :key="klass.name">
        <thead>
        <tr class="d-table__klass" @click="toggleKlass(klass.name)">
          <th>
            <span :class="openKlasses.indexOf(klass.name) > -1 ? 'icon-arrow-down4' : 'icon-arrow-right4'"></span>
            {{ klass.name }}
          </th>
          <th>{{ formatHours(klass.minutes) }} vikartimer</th>
          <th>{{ klass.substitutes.length }} vikarer</th>
          <th>{{ klass.topSubstitute.name }}</th>
          <th>{{ formatPercent(klass.topSubstitute.minutes, klass.minutes) }}</th>
        </tr>
        <tr class="d-table__columns" v-if="openKlasses.indexOf(klass.name) > -1">
          <th>Fag</th>
          <th>Vikartimer</th>
          <th>Antal vikarer</th>
          <th>Mest brugte vikar</th>
          <th>Andel for mest brugte vikar</th>
        </tr>
        </thead>
        <tbody v-if="openKlasses.indexOf(klass.name) > -1">
        <tr v-for="subject in klass.subjects" :key="subject.name">
          <td>{{ subject.name }}</td>
          <td>{{ formatHours(subject.minutes) }}</td>
          <td>{{ subject.substitutes.length }}</td>
          <td>{{ subject.topSubstitute.name }}</td>
          <td>{{ formatPercent(subject.topSubstitute.minutes, subject.minutes) }}</td>
        </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import Loader from '../common/Loader'
import Datepickerrange from '../datepicker/DatePickerRange'
import schoolDataMixin from '../../mixins/schoolDataMixin'

export default {
  components: {Datepickerrange, Loader, ChangeSchoolYear},
  mixins: [schoolDataMixin],
  data () {
    return {
      openKlasses: []
    }
  },
  computed: {
    reportCalendars () {
      const klassCalendars = this.$store.getters.klassCalendars || []
      const clubCalendars = this.$store.getters.clubCalendars || []
      return klassCalendars.concat(clubCalendars)
    },
    klassRows () {
      const klasses = {}
      this.lessonsInRange.forEach(lesson => {
        const substitutes = this.getLessonSubstitutes(lesson)
        if (substitutes.length < 1) {
          return
        }
        const subjectName = lesson.title || 'Uden titel'
        const klassNames = this.getLessonKlasses(lesson)
          .map(klass => klass.name)
          .filter((name, idx, arr) => arr.indexOf(name) === idx)
        klassNames.forEach(klassName => {
          if (!klasses[klassName]) {
            klasses[klassName] = {name: klassName, substitutes: {}, subjects: {}}
          }
          const klass = klasses[klassName]
          if (!klass.subjects[subjectName]) {
            klass.subjects[subjectName] = {name: subjectName, substitutes: {}}
          }
          substitutes.forEach(sub => {
            const minutes = sub.minutes || this.getLessonMinutes(lesson)
            this.addSubstituteMinutes(klass.substitutes, sub, minutes)
            this.addSubstituteMinutes(klass.subjects[subjectName].substitutes, sub, minutes)
          })
        })
      })
      return Object.values(klasses)
        .map(klass => ({
          ...this.summarize(klass),
          subjects: Object.values(klass.subjects)
            .map(subject => this.summarize(subject))
            .sort((a, b) => b.minutes - a.minutes)
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'da', {numeric: true}))
    }
  },
  methods: {
    addSubstituteMinutes (hash, sub, minutes) {
      if (!hash[sub.calendar_id]) {
        hash[sub.calendar_id] = {name: sub.name || `Vikar ${sub.calendar_id}`, minutes: 0}
      }
      hash[sub.calendar_id].minutes += minutes
    },
    summarize (group) {
      const substitutes = Object.values(group.substitutes).sort((a, b) => b.minutes - a.minutes)
      return {
        name: group.name,
        substitutes,
        minutes: substitutes.reduce((sum, sub) => sum + sub.minutes, 0),
        topSubstitute: substitutes[0] || {name: '-', minutes: 0}
      }
    },
    toggleKlass (name) {
      const idx = this.openKlasses.indexOf(name)
      if (idx < 0) {
        this.openKlasses.push(name)
      } else {
        this.openKlasses.splice(idx, 1)
      }
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.school-data {
  margin: 1rem;
  max-width: 1440px;
}

.school-data__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 1100px;
}

.school-data__header__left,
.school-data__header__right {
  display: flex;
  align-items: center;
}

.school-data__header h1 {
  margin-right: 1rem;
}

.text-explainer {
  max-width: 900px;
  color: #728195;
  margin-bottom: 1rem;
}

.loader-container {
  text-align: center;
}

.d-link {
  color: var(--blue);
  cursor: pointer;
  margin-right: 1rem;
}

.d-link:hover {
  text-decoration: underline;
}

.d-table {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-collapse: collapse;
  background: #fff;
  margin-bottom: 0.5rem;
  table-layout: fixed;
}

.d-table th,
.d-table td {
  padding: 0.5rem 1rem;
  text-align: right;
}

.d-table th:first-child,
.d-table td:first-child {
  text-align: left;
  width: 260px;
}

.d-table__klass {
  cursor: pointer;
  font-size: 14px;
}

.d-table__klass:hover {
  background: aliceblue;
}

.d-table__klass span {
  margin-right: 0.5rem;
}

.d-table__columns {
  background: #f6f9fc;
  color: #8898aa;
  text-transform: uppercase;
  font-size: 10px;
}

.d-table tbody tr {
  border-top: 1px solid #e3e8ee;
}

.d-table tbody tr:nth-child(even) {
  background: #f9fafb;
}
</style>



// WEBPACK FOOTER //
// SchoolDataSubContinuity.vue?f8915eba